/**
 * Infographics (Nov 2026): Funnel, Breakdown (stacked share bar), HeatGrid (weekday x week activity), Meter (segmented
 * progress). Square-cornered geometry only (no circle motifs). Every graphic is decorative over a real, readable list or
 * table: role="img" + aria-label on the picture, the numbers in text beside it or in a visually hidden table.
 * Numbers arrive already formatted where they are shown; `format` renders counts in the caller's locale.
 */
import type { CSSProperties, ReactNode } from 'react';

type Fmt = (n: number) => string;
const plain: Fmt = (n) => String(n);
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

/** Tapering horizontal bars, one per stage, each sized against the first stage, with the step-to-step conversion. */
export function Funnel({ steps, label, format = plain, formatPct = (n) => `${n}%` }: {
  steps: readonly { label: ReactNode; value: number }[]; label: string; format?: Fmt; formatPct?: Fmt;
}) {
  const top = Math.max(steps[0]?.value ?? 0, 1);
  return (
    <ol className="qm-funnel" aria-label={label}>
      {steps.map((s, i) => {
        const prev = i > 0 ? steps[i - 1]!.value : null;
        return (
          <li key={i} className="qm-funnel__step" style={{ '--qm-w': `${Math.max(4, (s.value / top) * 100)}%`, '--qm-i': i } as CSSProperties}>
            <span className="qm-funnel__label">{s.label}</span>
            <span className="qm-funnel__track" aria-hidden="true"><span className="qm-funnel__bar" /></span>
            <b className="qm-funnel__value">{format(s.value)}</b>
            <small className="qm-funnel__conv">{prev !== null ? formatPct(pct(s.value, prev)) : ''}</small>
          </li>
        );
      })}
    </ol>
  );
}

/** One stacked bar of shares plus a legend with counts and percentages (up to six parts; the rest fold into "other"). */
export function Breakdown({ parts, label, otherLabel = 'Other', format = plain, formatPct = (n) => `${n}%` }: {
  parts: readonly { label: ReactNode; value: number }[]; label: string; otherLabel?: ReactNode; format?: Fmt; formatPct?: Fmt;
}) {
  const sorted = [...parts].filter((p) => p.value > 0).sort((a, b) => b.value - a.value);
  const head = sorted.slice(0, 5);
  const rest = sorted.slice(5).reduce((s, p) => s + p.value, 0);
  const shown = rest > 0 ? [...head, { label: otherLabel, value: rest }] : head;
  const total = shown.reduce((s, p) => s + p.value, 0);
  return (
    <figure className="qm-breakdown">
      <div className="qm-breakdown__bar" role="img" aria-label={label}>
        {shown.map((p, i) => <span key={i} data-series={i + 1} style={{ flexGrow: p.value, '--qm-i': i } as CSSProperties} />)}
      </div>
      <ul className="qm-breakdown__legend">
        {shown.map((p, i) => (
          <li key={i} data-series={i + 1}><i aria-hidden="true" /><span>{p.label}</span><b>{format(p.value)}</b><small>{formatPct(pct(p.value, total))}</small></li>
        ))}
      </ul>
    </figure>
  );
}

/** Activity grid: rows (weekdays) x columns (weeks), five intensity levels, with a hidden table of the raw counts. */
export function HeatGrid({ rows, cols, values, label, format = plain, lessLabel = 'Less', moreLabel = 'More' }: {
  rows: readonly string[]; cols: readonly string[]; values: readonly (readonly number[])[]; label: string; format?: Fmt; lessLabel?: ReactNode; moreLabel?: ReactNode;
}) {
  const max = Math.max(1, ...values.flat());
  const level = (v: number) => (v <= 0 ? 0 : Math.min(4, Math.ceil((v / max) * 4)));
  return (
    <figure className="qm-heat">
      <div className="qm-heat__grid" role="img" aria-label={label} style={{ '--qm-cols': cols.length } as CSSProperties}>
        {rows.map((r, ri) => (
          <div key={r} className="qm-heat__row">
            <span className="qm-heat__rowlabel" aria-hidden="true">{r}</span>
            {cols.map((c, ci) => {
              const v = values[ri]?.[ci] ?? 0;
              return <span key={c} className="qm-heat__cell" data-level={level(v)} title={`${r} · ${c}: ${format(v)}`} style={{ '--qm-i': ci + ri } as CSSProperties} />;
            })}
          </div>
        ))}
      </div>
      <figcaption className="qm-heat__scale" aria-hidden="true">
        <span>{lessLabel}</span>{[0, 1, 2, 3, 4].map((l) => <span key={l} className="qm-heat__cell" data-level={l} />)}<span>{moreLabel}</span>
      </figcaption>
      <table className="qm-sr-only">
        <caption>{label}</caption>
        <thead><tr><th scope="col" />{cols.map((c) => <th key={c} scope="col">{c}</th>)}</tr></thead>
        <tbody>{rows.map((r, ri) => <tr key={r}><th scope="row">{r}</th>{cols.map((c, ci) => <td key={c}>{format(values[ri]?.[ci] ?? 0)}</td>)}</tr>)}</tbody>
      </table>
    </figure>
  );
}

/** Segmented progress meter (e.g. 3 of 6 connected). Exposed as a meter; the caption carries the words. */
export function Meter({ value, max, label, caption, tone = 'accent' }: { value: number; max: number; label: string; caption?: ReactNode; tone?: 'accent' | 'positive' | 'warning' }) {
  const segs = Math.max(1, Math.min(12, Math.round(max)));
  const filled = Math.max(0, Math.min(segs, Math.round((value / Math.max(max, 1)) * segs)));
  return (
    <div className="qm-meter" data-tone={tone}>
      <div className="qm-meter__segs" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={value}>
        {Array.from({ length: segs }, (_, i) => <span key={i} data-on={i < filled ? 'true' : undefined} style={{ '--qm-i': i } as CSSProperties} />)}
      </div>
      {caption ? <p className="qm-meter__caption">{caption}</p> : null}
    </div>
  );
}
